import { useState } from 'react';
import { Menu, X, LogIn, LayoutDashboard, Sprout, MapPin, Compass } from 'lucide-react';

interface HeaderProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  isAdmin: boolean;
  onLogout: () => void;
}

export default function Header({ currentPage, onNavigate, isAdmin, onLogout }: HeaderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showAnnouncement, setShowAnnouncement] = useState(true);

  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'memories', label: 'Memories' },
    { id: 'events', label: 'Events' },
    { id: 'features', label: 'Features' },
    { id: 'feedback', label: 'Guest Book' },
  ];

  const handleNavClick = (id: string) => {
    onNavigate(id);
    setIsOpen(false);
  };

  return (
    <div className="sticky top-0 z-40 w-full shadow-xs">
      {/* Botanical Announcement Bar */}
      {showAnnouncement && (
        <div className="announcement-bar px-4 relative flex items-center justify-center text-xs tracking-wide">
          <div className="flex items-center gap-2 max-w-7xl text-center px-6">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-emerald-100/90 font-medium">
              Private Family Estate & Sanctuary —{' '}
              <a
                href="https://maps.app.goo.gl/VCtYoxuA4cLNtSZHA"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-200 hover:text-white font-semibold underline underline-offset-2 transition-colors inline-flex items-center gap-1"
              >
                <MapPin size={11} className="inline" /> Sirudavoor, Chengalpattu
              </a>
            </span>
          </div>
          <button
            onClick={() => setShowAnnouncement(false)}
            aria-label="Close announcement"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-emerald-200/60 hover:text-white p-1 rounded-full transition-colors cursor-pointer"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Main Luxury Navigation Bar */}
      <header
        id="app-header"
        className="w-full bg-[#FDFCF9]/95 backdrop-blur-md border-b border-[#E6E1D4]/80 px-4 sm:px-6 md:px-8 transition-all duration-200"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between h-[68px]">
          {/* Logo Brand Emblem */}
          <button
            id="nav-logo"
            onClick={() => handleNavClick('home')}
            className="flex items-center gap-3 cursor-pointer group text-left transition-transform duration-200 active:scale-98"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#14532D] to-[#0F3820] flex items-center justify-center shadow-md shadow-emerald-950/15 group-hover:scale-105 transition-transform duration-300">
              <Sprout size={20} className="text-[#DCFCE7]" strokeWidth={2} />
            </div>
            <div className="flex flex-col">
              <span className="font-serif font-bold text-lg sm:text-xl text-[#1A241F] tracking-tight leading-tight group-hover:text-[#14532D] transition-colors">
                Mithran Farm House
              </span>
              <span className="font-sans text-[10px] font-semibold tracking-widest text-[#65756C] uppercase">
                Estate & Sanctuary
              </span>
            </div>
          </button>

          {/* Desktop Nav Links */}
          <nav id="desktop-nav" className="hidden md:flex items-center gap-1.5 lg:gap-2">
            {navItems.map((item) => {
              const isActive = currentPage === item.id;
              return (
                <button
                  key={item.id}
                  id={`nav-${item.id}`}
                  onClick={() => handleNavClick(item.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                    isActive
                      ? 'bg-[#14532D] text-white shadow-sm shadow-emerald-950/20'
                      : 'text-[#37463E] hover:text-[#14532D] hover:bg-[#F0FDF4]'
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* Right Action Zone */}
          <div className="hidden md:flex items-center gap-3">
            {isAdmin ? (
              <div className="flex items-center gap-2">
                <button
                  id="header-admin-dash"
                  onClick={() => handleNavClick('admin')}
                  className={`btn-estate-primary text-xs py-2 px-4 gap-2 ${
                    currentPage === 'admin' ? 'ring-2 ring-emerald-400 ring-offset-1' : ''
                  }`}
                >
                  <LayoutDashboard size={14} />
                  Estate Admin
                </button>
                <button
                  onClick={() => { onLogout(); }}
                  className="text-xs font-medium text-[#65756C] hover:text-red-700 px-3 py-2 rounded-full hover:bg-red-50 transition-colors cursor-pointer"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <button
                id="header-admin-login"
                onClick={() => handleNavClick('admin')}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium text-[#4B5E54] hover:text-[#14532D] hover:bg-[#F0FDF4] border border-[#E6E1D4] hover:border-[#14532D]/30 transition-all cursor-pointer"
              >
                <LogIn size={13} className="text-[#65756C]" />
                <span>Family Login</span>
              </button>
            )}
          </div>

          {/* Mobile Actions & Menu Toggle */}
          <div className="md:hidden flex items-center gap-2">
            {isAdmin && (
              <button
                onClick={() => handleNavClick('admin')}
                className="p-2 rounded-xl text-[#14532D] bg-[#F0FDF4] border border-[#DCFCE7] cursor-pointer"
                title="Estate Admin"
              >
                <LayoutDashboard size={18} />
              </button>
            )}
            <button
              id="mobile-nav-toggle"
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-xl text-[#1A241F] hover:bg-[#F0FDF4] transition-colors cursor-pointer"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Slide-Down Menu */}
        {isOpen && (
          <div
            id="mobile-nav-overlay"
            className="md:hidden border-t border-[#E6E1D4] py-4 px-2 animate-slideDown bg-[#FDFCF9]"
          >
            <div className="flex flex-col gap-1 pb-4">
              {navItems.map((item) => {
                const isActive = currentPage === item.id;
                return (
                  <button
                    key={item.id}
                    id={`mobile-nav-${item.id}`}
                    onClick={() => handleNavClick(item.id)}
                    className={`text-left px-4 py-3 rounded-xl text-sm font-medium transition-colors cursor-pointer ${
                      isActive
                        ? 'bg-[#14532D] text-white'
                        : 'text-[#37463E] hover:bg-[#F0FDF4] hover:text-[#14532D]'
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>

            <div className="border-t border-[#E6E1D4] pt-4 flex flex-col gap-2">
              <a
                href="https://maps.app.goo.gl/VCtYoxuA4cLNtSZHA"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-xs font-medium text-[#14532D] bg-[#F0FDF4] border border-[#DCFCE7]"
              >
                <Compass size={14} />
                Get Directions to Sirudavoor
              </a>

              {!isAdmin ? (
                <button
                  id="mobile-nav-admin-login"
                  onClick={() => handleNavClick('admin')}
                  className="btn-estate-primary w-full justify-center py-2.5 text-xs mt-1"
                >
                  <LogIn size={14} />
                  Family & Admin Login
                </button>
              ) : (
                <>
                  <button
                    id="mobile-nav-admin-dash"
                    onClick={() => handleNavClick('admin')}
                    className="btn-estate-primary w-full justify-center py-2.5 text-xs mt-1"
                  >
                    <LayoutDashboard size={14} />
                    Estate Admin Dashboard
                  </button>
                  <button
                    id="mobile-nav-logout"
                    onClick={() => { onLogout(); setIsOpen(false); }}
                    className="w-full py-2.5 text-xs text-center font-medium text-red-700 bg-red-50/70 border border-red-200/50 rounded-xl cursor-pointer"
                  >
                    Sign Out
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </header>
    </div>
  );
}
